import React from 'react';
import { Mail, Phone } from 'lucide-react';
import logo from '../assets/logo.png';

const LinkedInIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/>
  </svg>
);

const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932 6.064-6.932zm-1.294 19.497h2.039L6.486 3.24H4.298l13.309 17.41z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
  </svg>
);

const FacebookIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
  </svg>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#05111d] text-white pt-16 md:pt-24 pb-12 px-8 md:px-16 overflow-hidden border-t border-white/5">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16 mb-12 md:mb-20">
          
          <div className="lg:col-span-1 flex flex-col items-center md:items-start text-center md:text-left">
            <div className="mb-8">
              <img 
                src={logo} 
                alt="LCaaS Logo" 
                className="h-14 w-auto object-contain" 
              />
            </div>
            
            <div className="hidden md:block">
              <p className="text-sm text-white/40 leading-relaxed font-normal mb-8 max-w-xs">
                Providing premium legal and compliance solutions as a service. Led by 25+ years of cross-industry expertise.
              </p>
              <div className="flex gap-4">
                {[LinkedInIcon, XIcon, InstagramIcon, FacebookIcon].map((Icon, i) => (
                  <a 
                    key={i} 
                    href="/" 
                    className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-white/40 hover:text-[#78BE20] hover:border-[#78BE20] transition-all duration-300"
                  >
                    <Icon />
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="hidden md:block">
            <h4 className="text-base font-serif text-[#78BE20] mb-8">Quick links</h4>
            <ul className="space-y-4">
              {['Practice areas', 'About LCaaS', 'Resources', 'Careers', 'Contact'].map((item) => (
                <li key={item}>
                  <a href="/" className="text-sm text-white/50 hover:text-white transition-colors flex items-center group font-normal">
                    <span className="w-0 group-hover:w-4 overflow-hidden transition-all duration-300 h-[1px] bg-[#78BE20] mr-0 group-hover:mr-2" />
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="hidden lg:block">
            <h4 className="text-base font-serif text-[#78BE20] mb-8">Practice areas</h4>
            <ul className="space-y-4">
              {[
                'Banking & insurance', 
                'Fintech & start-ups', 
                'Global capability centres', 
                'Business houses'
              ].map((item) => (
                <li key={item}>
                  <span className="text-sm text-white/50 font-normal">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="hidden md:block">
            <h4 className="text-base font-serif text-[#78BE20] mb-8">Reach out</h4>
            <div className="space-y-6">
              <a href="mailto:anshoo@lcaas.in" className="flex items-center gap-4 group">
                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-[#78BE20] group-hover:bg-[#78BE20] group-hover:text-[#0c3754] transition-all">
                  <Mail size={14} />
                </div>
                <span className="text-sm text-white/50 group-hover:text-white transition-colors font-normal">anshoo@lcaas.in</span>
              </a>
              <a href="tel:+919810476281" className="flex items-center gap-4 group">
                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-[#78BE20] group-hover:bg-[#78BE20] group-hover:text-[#0c3754] transition-all">
                  <Phone size={14} />
                </div>
                <span className="text-sm text-white/50 group-hover:text-white transition-colors font-normal">+91-9810476281</span>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="pt-8 md:pt-12 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-6 text-center md:text-left">
          <div className="space-y-2">
            <p className="text-xs text-white/20 font-normal">
              © {currentYear} Legal & compliance as a service. All rights reserved.
            </p>
            <p className="text-[10px] text-white/20 font-normal tracking-wide">
              Developed by <a href="http://codezisoft.com/" target="_blank" rel="noopener noreferrer" className="hover:text-[#78BE20] transition-colors underline underline-offset-4 decoration-white/10">codezisoft</a>
            </p>
          </div>
          
          <div className="flex gap-8">
            <a href="/" className="text-xs text-white/20 hover:text-white transition-colors font-normal">Privacy policy</a>
            <a href="/" className="text-xs text-white/20 hover:text-white transition-colors font-normal">Terms of service</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;